import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { ApiError } from '@/lib/api/client';
import { ErrorBanner, expiryLabel, useDebounced } from '../borrow/ui';
import { useDispensingSettings } from '../useNearExpiry';
import { earlierBatchFor, pickBatches } from './batchPicking';
import { useIssueTransfer, useTransferStock } from './useTransfers';
import type { IndentDetail, StockTransferDetail, TransferStockBatch, TransferStore } from './types';

interface Line {
  batch: TransferStockBatch;
  quantity: string;
  reason: string;
  remarks: string;
}

const selectClass = 'h-9 w-full rounded-[var(--radius)] border border-input bg-card px-2 text-[13px]';
const qtyOf = (l: Line) => Number(l.quantity) || 0;

/**
 * Issue a transfer: pick the two stores, add batches from the source store's
 * shelf, set quantities. Batches are listed earliest-expiry first; picking a
 * later one while an earlier one could cover the line asks for the same reason
 * a sale does. The server re-checks stock, expiry and that rule under row locks.
 *
 * With `indent`, this is "send stock for an indent": the stores are the
 * indent's, only its medicines are offered, and the lines start filled
 * earliest-expiry first up to what the indent still needs.
 */
export default function NewTransferDialog({ stores, defaultFromId, indent, onClose, onIssued }: {
  stores: TransferStore[];
  defaultFromId?: number;
  indent?: IndentDetail;
  onClose: () => void;
  onIssued: (t: StockTransferDetail) => void;
}) {
  const active = stores.filter((s) => s.active);
  const needs = useMemo(
    () => (indent ? indent.items.filter((i) => i.qty_remaining > 0).map((i) => ({ medicine_id: i.medicine_id, quantity: i.qty_remaining, name: i.item_name })) : []),
    [indent]
  );
  const indentMedicineIds = useMemo(() => needs.map((n) => n.medicine_id), [needs]);
  const [fromId, setFromId] = useState<number | null>(indent?.supplying_store_id ?? defaultFromId ?? active[0]?.id ?? null);
  const [toId, setToId] = useState<number | null>(indent?.requesting_store_id ?? null);
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [notes, setNotes] = useState(indent ? `For ${indent.indent_number}` : '');
  const [formError, setFormError] = useState('');
  const debounced = useDebounced(search.trim());
  const stockQuery = useTransferStock(fromId, indent ? '' : debounced, indent ? indentMedicineIds : undefined);
  const issue = useIssueTransfer();
  const { batch_selection_reasons: reasons } = useDispensingSettings();

  // Every batch seen on the sending shelf so far — the earlier-batch check
  // needs the whole shelf, not just the current search results.
  const [seen, setSeen] = useState<Map<number, TransferStockBatch>>(new Map());
  useEffect(() => {
    if (!stockQuery.data) return;
    setSeen((m) => { const n = new Map(m); stockQuery.data.forEach((b) => n.set(b.inventory_id, b)); return n; });
  }, [stockQuery.data]);
  const shelf = useMemo(() => [...seen.values()], [seen]);

  // Changing the source store empties the list: its batches belong to the old store.
  useEffect(() => { if (!indent) { setLines([]); setSeen(new Map()); } }, [fromId, indent]);
  useEffect(() => { if (toId === fromId) setToId(null); }, [fromId, toId]);

  // Indent mode: fill once, earliest expiry first, when the shelf arrives.
  const filled = useRef(false);
  const [short, setShort] = useState<Array<{ name: string; missing: number }>>([]);
  useEffect(() => {
    if (!indent || filled.current || !stockQuery.data) return;
    filled.current = true;
    const picked = pickBatches(needs, stockQuery.data);
    setLines(picked.lines.map((p) => ({ batch: p.batch, quantity: String(p.quantity), reason: '', remarks: '' })));
    setShort(picked.short.map((s) => ({ name: needs.find((n) => n.medicine_id === s.medicine_id)?.name ?? '', missing: s.missing })));
  }, [indent, needs, stockQuery.data]);

  const stock = useMemo(() => stockQuery.data ?? [], [stockQuery.data]);
  const toStore = stores.find((s) => s.id === toId);
  const asPicked = (ls: Line[]) => ls.map((l) => ({ batch: l.batch, quantity: qtyOf(l) }));
  const earlierFor = (l: Line) => {
    const picked = asPicked(lines);
    return earlierBatchFor(picked[lines.indexOf(l)]!, picked, shelf);
  };

  const add = (batch: TransferStockBatch) => {
    if (lines.some((l) => l.batch.inventory_id === batch.inventory_id)) return;
    setLines((ls) => [...ls, { batch, quantity: '', reason: '', remarks: '' }]);
  };
  const update = (id: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.batch.inventory_id === id ? { ...l, ...patch } : l)));

  const submit = () => {
    setFormError('');
    if (!fromId || !toId) return setFormError('Choose the store sending the stock and the store receiving it.');
    if (!lines.length) return setFormError('Add at least one batch.');
    for (const l of lines) {
      const q = Number(l.quantity);
      if (!Number.isInteger(q) || q <= 0) return setFormError(`Enter a quantity for ${l.batch.generic_name} batch ${l.batch.batch_number}.`);
      if (q > l.batch.quantity_available) return setFormError(`Only ${l.batch.quantity_available} of batch ${l.batch.batch_number} are on the shelf.`);
      const earlier = earlierFor(l);
      if (earlier && !l.reason) return setFormError(`Batch ${l.batch.batch_number} expires after batch ${earlier.batch_number} — choose why the later one is being sent.`);
      if (l.reason === 'other' && !l.remarks.trim()) return setFormError(`Add a remark for batch ${l.batch.batch_number}.`);
    }
    if (indent) {
      for (const n of needs) {
        const total = lines.filter((l) => l.batch.medicine_id === n.medicine_id).reduce((s, l) => s + qtyOf(l), 0);
        if (total > n.quantity) return setFormError(`${n.name}: the indent still needs ${n.quantity}, not ${total}.`);
      }
    }
    issue.mutate({
      from_store_id: fromId,
      to_store_id: toId,
      notes: notes.trim() || undefined,
      ...(indent ? { indent_id: indent.id } : {}),
      items: lines.map((l) => ({
        inventory_id: l.batch.inventory_id,
        quantity: Number(l.quantity),
        ...(l.reason ? { batch_selection_reason: l.reason, batch_selection_remarks: l.remarks.trim() || undefined } : {}),
      })),
    }, { onSuccess: onIssued });
  };

  const serverError = issue.error instanceof Error ? issue.error.message : null;
  // The server checks earliest-expiry against the whole shelf; the screen may
  // not have seen the earlier batch. When it asks, offer a reason on every line.
  const askAllReasons = issue.error instanceof ApiError && issue.error.details?.reason === 'batch_selection_required';

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[760px]">
        <DialogHeader>
          <DialogTitle>{indent ? `Send stock for ${indent.indent_number}` : 'New stock transfer'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="From store">
              <select className={selectClass} value={fromId ?? ''} disabled={!!indent} onChange={(e) => setFromId(e.target.value ? Number(e.target.value) : null)}>
                {active.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FormField>
            <FormField label="To store" hint={toStore?.auto_receive_transfers ? 'This store takes transfers in as soon as they are issued.' : 'The receiving store confirms what arrived.'}>
              <select className={selectClass} value={toId ?? ''} disabled={!!indent} onChange={(e) => setToId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Choose…</option>
                {active.filter((s) => s.id !== fromId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FormField>
          </div>

          {indent && short.length > 0 && (
            <div className="rounded-[var(--radius)] bg-warn-soft px-3 py-2 text-[12.5px] text-warn-ink">
              Not enough on the shelf to fill the indent: {short.map((s) => `${s.name} (${s.missing} short)`).join(', ')}.
              Send what there is; the indent stays open for the rest, or close it from the indent.
            </div>
          )}

          {!indent && (
            <FormField label="Add batches from the sending store">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Medicine, brand or batch" className="pl-8" />
              </div>
            </FormField>
          )}
          {indent && <div className="text-[12.5px] font-semibold">Batches of the indented medicines on the sending shelf</div>}
          <div className="max-h-[180px] overflow-y-auto rounded-[var(--radius)] border border-border">
            {stockQuery.isLoading ? (
              <div className="p-3 text-[12.5px] text-muted-foreground">Loading…</div>
            ) : stock.length === 0 ? (
              <div className="p-3 text-[12.5px] text-muted-foreground">{debounced ? 'Nothing matches on this store’s shelf.' : 'No sendable stock in this store.'}</div>
            ) : stock.map((b) => {
              const added = lines.some((l) => l.batch.inventory_id === b.inventory_id);
              return (
                <button
                  key={b.inventory_id}
                  type="button"
                  disabled={added}
                  onClick={() => add(b)}
                  className="flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-left text-[13px] last:border-b-0 hover:bg-muted disabled:opacity-50"
                >
                  <span className="min-w-0">
                    <span className="font-semibold">{b.generic_name}</span> {b.strength}
                    {b.brand_name && <span className="text-muted-foreground"> · {b.brand_name}</span>}
                    <span className="block text-[11.5px] text-muted-foreground">Batch {b.batch_number} · expires {expiryLabel(b.expiry_date)}</span>
                  </span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">{added ? 'Added' : `${b.quantity_available} on shelf`}</span>
                </button>
              );
            })}
          </div>

          {lines.length > 0 && (
            <div className="space-y-2">
              {lines.map((l) => {
                const earlier = earlierFor(l);
                return (
                  <div key={l.batch.inventory_id} className="rounded-[var(--radius)] border border-border p-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-0 flex-1 text-[13px]">
                        <span className="font-semibold">{l.batch.generic_name}</span> {l.batch.strength}
                        <span className="block text-[11.5px] text-muted-foreground">Batch {l.batch.batch_number} · expires {expiryLabel(l.batch.expiry_date)} · {l.batch.quantity_available} on shelf</span>
                      </div>
                      <Input
                        type="number" min={1} max={l.batch.quantity_available} inputMode="numeric"
                        aria-label={`Quantity of batch ${l.batch.batch_number}`}
                        value={l.quantity} onChange={(e) => update(l.batch.inventory_id, { quantity: e.target.value })}
                        className="w-24" placeholder="Qty"
                      />
                      <Button variant="ghost" size="sm" aria-label="Remove line" onClick={() => setLines((ls) => ls.filter((x) => x !== l))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {(earlier || askAllReasons) && (
                      <div className="mt-2 grid gap-2 rounded-md bg-warn-soft p-2 text-[12px] text-warn-ink sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          {earlier
                            ? <>Batch {earlier.batch_number} expires sooner ({expiryLabel(earlier.expiry_date)}). Send that one, or say why not.</>
                            : <>If this isn’t the earliest-expiring batch on the shelf, say why it is being sent.</>}
                        </div>
                        <select className={selectClass} value={l.reason} onChange={(e) => update(l.batch.inventory_id, { reason: e.target.value })} aria-label="Reason for sending the later batch">
                          <option value="">Reason…</option>
                          {reasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        <Input value={l.remarks} onChange={(e) => update(l.batch.inventory_id, { remarks: e.target.value })} placeholder="Remarks" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <FormField label="Notes (optional)">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Weekly ward top-up" />
          </FormField>
          <ErrorBanner message={formError || serverError} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={issue.isPending}>
            {toStore?.auto_receive_transfers ? 'Transfer now' : 'Issue transfer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
